import type {
  ActivityEvent,
  Asset,
  Audit,
  BootstrapData,
  Contact,
  CustomerOrder,
  ExpenseRecord,
  FinancePlan,
  Goal,
  Profile,
  Project,
  Review,
  Skill,
  SkillEvidence,
  Task,
  Transaction
} from "../types";
import { getRollingReviewPeriod } from "../../shared/reviewPeriod";
import {
  CAREER_KEYWORDS,
  createGrowthSnapshot,
  GROWTH_DIMENSIONS,
  ORDER_PROGRESS_POINTS,
  PHYSIQUE_KEYWORDS,
  PROJECT_PROGRESS_POINTS
} from "../../shared/growthProgression";
import {
  createSkillProgress,
  inferExerciseSkills,
  resolveSkillDefaults,
  type SkillGrowthDimensionId,
  type SkillSphere
} from "../../shared/skillSystem";

const storageKey = "earth-online:v3:browser-trial";

interface TrialSkillEvent {
  id: string;
  skillId: string;
  eventId: string;
  xp: number;
  createdAt: string;
  evidenceLevel?: string;
  reason?: string;
}

interface TrialTrashItem {
  id: string;
  entityType: string;
  label: string;
  deletedAt: string;
  snapshot: unknown;
}

interface TrialStore {
  profile: Profile | null;
  goals: Goal[];
  projects: Project[];
  tasks: Task[];
  events: ActivityEvent[];
  skills: Skill[];
  reviews: Review[];
  contacts: Contact[];
  customerOrders: CustomerOrder[];
  transactions: Transaction[];
  assets: Asset[];
  financialPlans: FinancePlan[];
  expenses: ExpenseRecord[];
  skillEvents: TrialSkillEvent[];
  audits: Audit[];
  trash: TrialTrashItem[];
}

const emptyStore = (): TrialStore => ({
  profile: null,
  goals: [],
  projects: [],
  tasks: [],
  events: [],
  skills: [],
  reviews: [],
  contacts: [],
  customerOrders: [],
  transactions: [],
  assets: [],
  financialPlans: [],
  expenses: [],
  skillEvents: [],
  audits: [],
  trash: []
});

function readStore() {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return emptyStore();
    const parsed = JSON.parse(raw) as Partial<TrialStore>;
    const fallback = emptyStore();
    return {
      ...fallback,
      ...parsed,
      profile: parsed.profile ?? null,
      goals: parsed.goals ?? [],
      projects: parsed.projects ?? [],
      tasks: parsed.tasks ?? [],
      events: parsed.events ?? [],
      skills: parsed.skills ?? [],
      reviews: parsed.reviews ?? [],
      contacts: parsed.contacts ?? [],
      customerOrders: parsed.customerOrders ?? [],
      transactions: parsed.transactions ?? [],
      assets: parsed.assets ?? [],
      financialPlans: parsed.financialPlans ?? [],
      skillEvents: parsed.skillEvents ?? [],
      audits: parsed.audits ?? [],
      trash: parsed.trash ?? []
    };
  } catch {
    return emptyStore();
  }
}

function writeStore(store: TrialStore) {
  window.localStorage.setItem(storageKey, JSON.stringify(store));
}

function id() {
  return crypto.randomUUID();
}

function now() {
  return new Date().toISOString();
}

function audit(store: TrialStore, action: string, entityType: string, entityId: string) {
  store.audits.unshift({
    id: id(),
    action,
    entity_type: entityType,
    entity_id: entityId,
    detail_json: "{}",
    created_at: now()
  });
}

function taskScore(task: Task) {
  const explanations: string[] = [];
  let score = Number(task.priority) * 8 + Number(task.value_score) * 7;
  if (task.priority >= 4) explanations.push("与当前高优目标直接相关");
  if (task.value_score >= 4) explanations.push("完成后价值较高");
  if (task.pinned) {
    score += 36;
    explanations.push("由你手动置顶");
  }
  if (task.external_commitment) {
    score += 14;
    explanations.push("涉及对外承诺");
  }
  if (explanations.length === 0) explanations.push("综合优先级与价值排序");
  return { score, explanations };
}

function bootstrap(store: TrialStore): BootstrapData {
  const contacts = store.contacts
    .map((contact, index) => ({
      ...contact,
      country: contact.country ?? "",
      contact_channel: contact.contact_channel ?? "",
      channel_account: contact.channel_account ?? "",
      customer_status: contact.customer_status ?? "PROSPECT",
      importance: contact.importance ?? "B",
      next_action: contact.next_action ?? "",
      sort_order: contact.sort_order ?? index + 1
    }))
    .sort((first, second) => first.sort_order - second.sort_order);
  const skillTotals = new Map<string, { xp: number; count: number; last: string | null }>();
  for (const event of store.skillEvents) {
    const current = skillTotals.get(event.skillId) ?? { xp: 0, count: 0, last: null };
    current.xp += event.xp;
    current.count += 1;
    if (!current.last || event.createdAt > current.last) current.last = event.createdAt;
    skillTotals.set(event.skillId, current);
  }
  const skills = store.skills.map((skill) => {
    const total = skillTotals.get(skill.id) ?? { xp: 0, count: 0, last: null };
    const defaults = resolveSkillDefaults({
      title: skill.title,
      kind: skill.kind,
      sphere: skill.sphere,
      category: skill.category,
      growthDimensionId: skill.growth_dimension_id
    });
    return {
      ...skill,
      kind: skill.kind ?? "SKILL",
      sphere: skill.sphere ?? defaults.sphere,
      category: skill.category ?? "综合",
      growth_dimension_id: skill.growth_dimension_id ?? defaults.growthDimensionId,
      auto_created: skill.auto_created ?? 0,
      source_key: skill.source_key ?? null,
      total_xp: total.xp,
      evidence_count: total.count,
      last_used_at: total.last,
      ...createSkillProgress(total.xp)
    };
  });
  const skillEvidence = store.skillEvents.flatMap<SkillEvidence>((entry) => {
    const event = store.events.find((item) => item.id === entry.eventId);
    if (!event) return [];
    return [{
      id: entry.id,
      skill_id: entry.skillId,
      activity_event_id: entry.eventId,
      event_title: event.title,
      event_type: event.event_type,
      occurred_at: event.occurred_at,
      xp_delta: entry.xp,
      evidence_level: entry.evidenceLevel ?? "PRACTICE",
      reason: entry.reason ?? "由已确认的事实记录产生。",
      created_at: entry.createdAt
    }];
  }).sort((first, second) => second.occurred_at.localeCompare(first.occurred_at));
  const projects = store.projects.map((project) => ({
    ...project,
    goal_title: store.goals.find((goal) => goal.id === project.goal_id)?.title ?? null,
    progress: null
  }));
  const tasks = store.tasks
    .map((task) => {
      const scored = taskScore(task);
      return {
        ...task,
        goal_title: store.goals.find((goal) => goal.id === task.goal_id)?.title ?? null,
        project_title:
          store.projects.find((project) => project.id === task.project_id)?.title ?? null,
        skill_title: skills.find((skill) => skill.id === task.skill_id)?.title ?? null,
        contact_id: task.contact_id ?? null,
        contact_name:
          contacts.find((contact) => contact.id === task.contact_id)?.name ?? null,
        recommendation_score: scored.score,
        explanations: scored.explanations
      };
    })
    .sort((first, second) => second.recommendation_score - first.recommendation_score);
  const financeTotals = [...new Set(store.transactions.map((item) => item.currency))].map(
    (currency) => {
      const matching = store.transactions.filter((item) => item.currency === currency);
      const income = matching
        .filter((item) => item.transaction_type === "INCOME")
        .reduce((sum, item) => sum + item.amount_minor, 0);
      const expense = matching
        .filter((item) => item.transaction_type === "EXPENSE")
        .reduce((sum, item) => sum + item.amount_minor, 0);
      return {
        currency,
        balance_minor: income - expense,
        income_minor: income,
        expense_minor: expense
      };
    }
  );
  const assetTotals = [...new Set(store.assets.map((item) => item.currency))].map((currency) => ({
    currency,
    total_minor: store.assets
      .filter((item) => item.currency === currency)
      .reduce((sum, item) => sum + Number(item.amount_minor), 0)
  }));
  const completed = store.tasks.filter((task) => task.status === "COMPLETED");
  const textOf = (record: Record<string, unknown>) =>
    Object.values(record)
      .filter((value) => typeof value === "string")
      .join(" ")
      .toLowerCase();
  const hasAny = (value: string, keywords: readonly string[]) =>
    keywords.some((keyword) => value.includes(keyword));
  const hasLinkedEntity = (event: ActivityEvent, types: string[]) => {
    try {
      const links = JSON.parse(event.links_json) as Array<{ entityType?: string }>;
      return links.some((link) => link.entityType && types.includes(link.entityType));
    } catch {
      return false;
    }
  };
  const goalDomains = new Map(store.goals.map((goal) => [goal.id, goal.domain]));
  const completedWorkTasks = completed.filter((task) =>
    Boolean(task.project_id || task.contact_id) ||
    hasAny(`${textOf(task as unknown as Record<string, unknown>)} ${goalDomains.get(task.goal_id ?? "") ?? ""}`, CAREER_KEYWORDS)
  );
  const completedBodyTasks = completed.filter((task) =>
    hasAny(textOf(task as unknown as Record<string, unknown>), PHYSIQUE_KEYWORDS)
  );
  const workEvents = store.events.filter((event) =>
    hasAny(textOf(event as unknown as Record<string, unknown>), CAREER_KEYWORDS) ||
    hasLinkedEntity(event, ["project", "contact", "customer_order", "order"])
  );
  const bodyEvents = store.events.filter((event) =>
    hasAny(textOf(event as unknown as Record<string, unknown>), PHYSIQUE_KEYWORDS)
  );
  const careerSkillEvidence = skills
    .filter((skill) => skill.growth_dimension_id === "attr-career")
    .reduce((sum, skill) => sum + Number(skill.evidence_count), 0);
  const bodySkillEvidence = skills
    .filter((skill) => skill.growth_dimension_id === "attr-physique")
    .reduce((sum, skill) => sum + Number(skill.evidence_count), 0);
  const experienceSkillEvidence = skills
    .filter((skill) => skill.growth_dimension_id === "attr-experience")
    .reduce((sum, skill) => sum + Number(skill.evidence_count), 0);
  const publishedPeriods = new Set(
    store.reviews
      .filter((review) => review.status === "PUBLISHED")
      .map((review) => `${review.review_type}:${review.period_start}:${review.period_end}`)
  );
  const careerPoints =
    completedWorkTasks.length * 2 +
    workEvents.reduce(
      (sum, event) => sum + 2 + (event.event_type === "RESULT" ? 4 : 0),
      0
    ) +
    store.projects.reduce(
      (sum, project) => sum + (PROJECT_PROGRESS_POINTS[project.status] ?? 0),
      0
    ) +
    store.customerOrders.reduce(
      (sum, order) => sum + (ORDER_PROGRESS_POINTS[order.status] ?? 0),
      0
    ) +
    careerSkillEvidence;
  const cnyAssets = store.assets.filter((asset) => asset.currency === "CNY");
  const cnyAssetValue = cnyAssets.reduce((sum, asset) => sum + Number(asset.amount_minor), 0) / 100;
  const experiencePoints =
    store.events.reduce(
      (sum, event) =>
        sum + 1 + (["RESULT", "LEARNING", "APPLICATION"].includes(event.event_type) ? 2 : 0),
      0
    ) +
    experienceSkillEvidence +
    publishedPeriods.size * 4 +
    store.projects.filter((project) => project.status === "COMPLETED").length * 12;
  const physiquePoints = bodyEvents.length * 2 + completedBodyTasks.length * 3 + bodySkillEvidence;
  const growthValues = {
    "attr-career": {
      value: careerPoints,
      count: completedWorkTasks.length + workEvents.length + store.projects.length + store.customerOrders.length,
      basis: `${workEvents.length} 条工作事实 · ${completedWorkTasks.length} 个完成事项 · ${store.projects.length + store.customerOrders.length} 个项目/订单`
    },
    "attr-wealth": {
      value: cnyAssetValue,
      count: cnyAssets.length,
      basis: `${cnyAssets.length} 项人民币资产现状 · 财务计划不计入已拥有财富`
    },
    "attr-experience": {
      value: experiencePoints,
      count: store.events.length + experienceSkillEvidence + publishedPeriods.size,
      basis: `${store.events.length} 条人生事实 · ${experienceSkillEvidence} 条经历技能实践 · ${publishedPeriods.size} 个有效复盘周期`
    },
    "attr-physique": {
      value: physiquePoints,
      count: bodyEvents.length + completedBodyTasks.length + bodySkillEvidence,
      basis: `${bodyEvents.length} 条运动事实 · ${completedBodyTasks.length} 个运动事项 · ${bodySkillEvidence} 条体魄实践`
    }
  } as const;
  const dimensions = GROWTH_DIMENSIONS.map((definition) => {
    const current = growthValues[definition.id];
    return createGrowthSnapshot({
      id: definition.id,
      value: current.value,
      evidenceCount: current.count,
      basis: current.basis
    });
  });

  return {
    profile: store.profile,
    goals: [...store.goals],
    projects,
    milestones: [],
    tasks,
    events: [...store.events].sort((a, b) => b.occurred_at.localeCompare(a.occurred_at)),
    skills,
    skillEvidence,
    dimensions,
    reviews: [...store.reviews].sort((a, b) => b.created_at.localeCompare(a.created_at)),
    contacts,
    customerOrders: store.customerOrders
      .map((order) => {
        const contact = contacts.find((item) => item.id === order.contact_id);
        return {
          ...order,
          contact_name: contact?.name ?? "已删除客户",
          contact_organization: contact?.organization ?? ""
        };
      })
      .sort((first, second) => second.updated_at.localeCompare(first.updated_at)),
    transactions: store.transactions.map((item) => ({
      ...item,
      subcategory: item.subcategory ?? ""
    })).sort((a, b) =>
      b.occurred_on.localeCompare(a.occurred_on)
    ),
    financeTotals,
    assets: [...store.assets].sort((a, b) => b.as_of_date.localeCompare(a.as_of_date)),
    assetTotals,
    financialPlans: [...store.financialPlans].sort((first, second) => {
      const statusOrder = { ACTIVE: 0, PAUSED: 1, COMPLETED: 2 };
      const statusDifference = statusOrder[first.status] - statusOrder[second.status];
      if (statusDifference !== 0) return statusDifference;
      return (first.target_date ?? "9999-12-31").localeCompare(
        second.target_date ?? "9999-12-31"
      );
    }),
    expenses: [...store.expenses],
    deleted: store.trash.map((item) => ({
      id: item.id,
      label: item.label,
      deleted_at: item.deletedAt,
      entity_type: item.entityType
    })),
    audits: store.audits.slice(0, 100),
    meta: {
      syncedAt: now(),
      ownerMode: "BROWSER_TRIAL",
      aiAvailable: false
    }
  };
}

function text(value: unknown) {
  return typeof value === "string" ? value : "";
}

function number(value: unknown, fallback = 0) {
  return Number.isFinite(Number(value)) ? Number(value) : fallback;
}

function bool(value: unknown) {
  return Boolean(value);
}

function awardTrialSkillEvidence(
  store: TrialStore,
  input: {
    skillId: string;
    eventId: string;
    timestamp: string;
    baseXp: number;
    difficulty: number;
    impact: number;
    evidenceLevel: string;
    reason: string;
  }
) {
  if (store.skillEvents.some((entry) => entry.skillId === input.skillId && entry.eventId === input.eventId)) {
    return 0;
  }
  const repeated = store.skillEvents.filter(
    (entry) => entry.skillId === input.skillId && entry.createdAt.slice(0, 10) === input.timestamp.slice(0, 10)
  ).length;
  const difficultyFactor = 0.6 + Math.min(5, Math.max(1, input.difficulty)) * 0.2;
  const impactFactor = 0.6 + Math.min(5, Math.max(1, input.impact)) * 0.2;
  const repetitionFactor = Math.max(0.25, 1 - repeated * 0.15);
  const xp = Math.max(1, Math.round(input.baseXp * difficultyFactor * impactFactor * 1.2 * repetitionFactor));
  store.skillEvents.push({
    id: id(),
    skillId: input.skillId,
    eventId: input.eventId,
    xp,
    createdAt: input.timestamp,
    evidenceLevel: input.evidenceLevel,
    reason: input.reason
  });
  return xp;
}

function syncTrialExerciseSkills(
  store: TrialStore,
  input: {
    eventId: string;
    text: string;
    timestamp: string;
    difficulty?: number;
    impact?: number;
  }
) {
  let awardedXp = 0;
  const skillIds: string[] = [];
  for (const template of inferExerciseSkills(input.text)) {
    let skill = store.skills.find(
      (item) => item.source_key === template.sourceKey || item.title === template.title
    );
    if (!skill) {
      const progress = createSkillProgress(0);
      skill = {
        id: id(),
        title: template.title,
        description: template.description,
        kind: "SKILL",
        sphere: template.sphere,
        category: template.category,
        growth_dimension_id: template.growthDimensionId,
        auto_created: 1,
        source_key: template.sourceKey,
        total_xp: 0,
        evidence_count: 0,
        last_used_at: null,
        ...progress,
        created_at: input.timestamp
      };
      store.skills.unshift(skill);
      audit(store, "AUTO_CREATE", "skills", skill.id);
    } else if (!skill.source_key) {
      skill.source_key = template.sourceKey;
    }
    skillIds.push(skill.id);
    awardedXp += awardTrialSkillEvidence(store, {
      skillId: skill.id,
      eventId: input.eventId,
      timestamp: input.timestamp,
      baseXp: 6,
      difficulty: input.difficulty ?? 2,
      impact: input.impact ?? 2,
      evidenceLevel: "AUTO_CONFIRMED",
      reason: "系统从已确认的运动事实中识别；经验只来自这条可追溯记录。"
    });
  }
  return { awardedXp, skillIds };
}

function create(store: TrialStore, kind: string, raw: unknown) {
  const input = raw as Record<string, unknown>;
  const entityId = id();
  const timestamp = now();

  if (kind === "goals") {
    store.goals.unshift({
      id: entityId,
      title: text(input.title),
      domain: text(input.domain),
      why_important: text(input.whyImportant),
      success_metric: text(input.successMetric),
      deadline: text(input.deadline) || null,
      status: "ACTIVE",
      priority: number(input.priority, 3),
      created_at: timestamp
    });
  } else if (kind === "projects") {
    store.projects.unshift({
      id: entityId,
      goal_id: text(input.goalId) || null,
      goal_title: null,
      title: text(input.title),
      outcome: text(input.outcome),
      next_action: text(input.nextAction),
      status: "ACTIVE",
      priority: number(input.priority, 3),
      risk: "NONE",
      deadline: text(input.deadline) || null,
      progress: null,
      created_at: timestamp
    });
  } else if (kind === "tasks") {
    store.tasks.unshift({
      id: entityId,
      goal_id: text(input.goalId) || null,
      goal_title: null,
      project_id: text(input.projectId) || null,
      project_title: null,
      skill_id: text(input.skillId) || null,
      skill_title: null,
      contact_id: text(input.contactId) || null,
      contact_name: null,
      title: text(input.title),
      detail: text(input.detail),
      status: "TODO",
      priority: number(input.priority, 3),
      value_score: number(input.valueScore, 3),
      difficulty: number(input.difficulty, 2),
      estimated_minutes: input.estimatedMinutes ? number(input.estimatedMinutes) : null,
      due_date: text(input.dueDate) || null,
      pinned: bool(input.pinned) ? 1 : 0,
      external_commitment: bool(input.externalCommitment) ? 1 : 0,
      completed_at: null,
      recommendation_score: 0,
      explanations: [],
      created_at: timestamp
    });
  } else if (kind === "events") {
    const links = Array.isArray(input.links) ? input.links : [];
    store.events.unshift({
      id: entityId,
      event_type: text(input.eventType),
      occurred_at: text(input.occurredAt) || timestamp,
      title: text(input.title),
      factual_content: text(input.factualContent),
      result_summary: text(input.resultSummary),
      next_action: text(input.nextAction),
      source_type: bool(input.aiAssisted) ? "AI_CONFIRMED" : "USER",
      ai_assisted: bool(input.aiAssisted) ? 1 : 0,
      links_json: JSON.stringify(links),
      created_at: timestamp
    });
    for (const link of links) {
      const candidate = link as { entityType?: unknown; entityId?: unknown };
      if (candidate.entityType === "skill" && typeof candidate.entityId === "string") {
        awardTrialSkillEvidence(store, {
          skillId: candidate.entityId,
          eventId: entityId,
          timestamp,
          baseXp: 6,
          difficulty: 2,
          impact: 2,
          evidenceLevel: "PRACTICE",
          reason: "由用户确认并关联到技能的事实记录产生。"
        });
      }
    }
    syncTrialExerciseSkills(store, {
      eventId: entityId,
      text: `${text(input.title)} ${text(input.factualContent)} ${text(input.resultSummary)}`,
      timestamp
    });
  } else if (kind === "skills") {
    const defaults = resolveSkillDefaults({
      title: text(input.title),
      kind: input.kind === "INTEREST" ? "INTEREST" : "SKILL",
      sphere: (["WORK", "LIFE"].includes(text(input.sphere)) ? text(input.sphere) : undefined) as SkillSphere | undefined,
      category: text(input.category),
      growthDimensionId: (["attr-career", "attr-experience", "attr-physique"].includes(text(input.growthDimensionId))
        ? text(input.growthDimensionId)
        : undefined) as SkillGrowthDimensionId | undefined
    });
    store.skills.unshift({
      id: entityId,
      title: text(input.title),
      description: text(input.description),
      kind: defaults.kind,
      sphere: defaults.sphere,
      category: text(input.category) || "综合",
      growth_dimension_id: defaults.growthDimensionId,
      auto_created: 0,
      source_key: null,
      total_xp: 0,
      evidence_count: 0,
      last_used_at: null,
      ...createSkillProgress(0),
      created_at: timestamp
    });
  } else if (kind === "contacts") {
    store.contacts.unshift({
      id: entityId,
      name: text(input.name),
      organization: text(input.organization),
      country: text(input.country),
      contact_channel: text(input.contactChannel),
      channel_account: text(input.channelAccount),
      customer_status: ["QUALIFYING", "ACTIVE", "PAUSED", "UNSUITABLE"].includes(
        text(input.customerStatus)
      )
        ? (text(input.customerStatus) as Contact["customer_status"])
        : "PROSPECT",
      importance: ["S", "A", "C"].includes(text(input.importance))
        ? (text(input.importance) as Contact["importance"])
        : "B",
      main_products: text(input.mainProducts),
      purchase_season: text(input.purchaseSeason),
      annual_purchase: text(input.annualPurchase),
      price_sensitivity: text(input.priceSensitivity),
      moq_acceptance: text(input.moqAcceptance),
      comm_style: text(input.commStyle),
      payment_habit: text(input.paymentHabit),
      risk_tags: text(input.riskTags),
      context: text(input.context),
      next_action: text(input.nextAction),
      next_followup: text(input.nextFollowup) || null,
      sort_order: store.contacts.length + 1,
      created_at: timestamp
    });
  } else if (kind === "customer-orders") {
    const amount = text(input.amount);
    const [whole = "0", decimal = ""] = amount.split(".");
    const contact = store.contacts.find((item) => item.id === text(input.contactId));
    if (!contact) throw new Error("请选择存在的客户");
    store.customerOrders.unshift({
      id: entityId,
      contact_id: contact.id,
      contact_name: contact.name,
      contact_organization: contact.organization,
      order_code: text(input.orderCode),
      files_json: "[]",
      title: text(input.title),
      images_json: JSON.stringify(Array.isArray(input.images) ? input.images : []),
      requirements: text(input.requirements),
      amount_minor: Number(whole) * 100 + Number(decimal.padEnd(2, "0")),
      currency: ["CNY", "EUR", "HKD", "JPY"].includes(text(input.currency))
        ? (text(input.currency) as CustomerOrder["currency"])
        : "USD",
      quantity: input.quantity ? number(input.quantity) : null,
      status: text(input.status) as CustomerOrder["status"],
      next_action: text(input.nextAction),
      next_followup: text(input.nextFollowup) || null,
      note: text(input.note),
      created_at: timestamp,
      updated_at: timestamp
    });
  } else if (kind === "transactions") {
    const amount = text(input.amount);
    const [whole = "0", decimal = ""] = amount.split(".");
    store.transactions.unshift({
      id: entityId,
      account_type: input.accountType === "BUSINESS" ? "BUSINESS" : "PERSONAL",
      transaction_type: input.transactionType === "INCOME" ? "INCOME" : "EXPENSE",
      amount_minor: Number(whole) * 100 + Number(decimal.padEnd(2, "0")),
      currency: text(input.currency),
      category: text(input.category),
      subcategory: text(input.subcategory),
      note: text(input.note),
      occurred_on: text(input.occurredOn),
      created_at: timestamp
    });
  } else if (kind === "assets") {
    const amount = text(input.amount);
    const [whole = "0", decimal = ""] = amount.split(".");
    store.assets.unshift({
      id: entityId,
      name: text(input.name),
      asset_type: ["CASH", "BANK", "INVESTMENT", "RECEIVABLE", "FIXED"].includes(
        text(input.assetType)
      )
        ? (text(input.assetType) as Asset["asset_type"])
        : "OTHER",
      amount_minor: Number(whole) * 100 + Number(decimal.padEnd(2, "0")),
      currency: text(input.currency) || "CNY",
      note: text(input.note),
      as_of_date: text(input.asOfDate),
      created_at: timestamp
    });
  } else if (kind === "financial-plans") {
    const amount = text(input.amount);
    const [whole = "0", decimal = ""] = amount.split(".");
    store.financialPlans.unshift({
      id: entityId,
      title: text(input.title),
      plan_type: input.planType === "SPENDING" ? "SPENDING" : "SAVING",
      amount_minor: Number(whole) * 100 + Number(decimal.padEnd(2, "0")),
      currency: (["USD", "EUR", "HKD", "JPY"].includes(text(input.currency))
        ? text(input.currency)
        : "CNY") as FinancePlan["currency"],
      cadence: (["MONTHLY", "YEARLY"].includes(text(input.cadence))
        ? text(input.cadence)
        : "ONE_TIME") as FinancePlan["cadence"],
      target_date: text(input.targetDate) || null,
      note: text(input.note),
      status: (["PAUSED", "COMPLETED"].includes(text(input.status))
        ? text(input.status)
        : "ACTIVE") as FinancePlan["status"],
      created_at: timestamp,
      updated_at: timestamp
    });
  } else if (kind === "profile") {
    store.profile = {
      id: "browser-owner",
      display_name: text(input.displayName) || null,
      timezone: text(input.timezone) || "Asia/Shanghai",
      current_version: text(input.currentVersion) || null,
      energy: input.energy ? number(input.energy) : null,
      focus: input.focus ? number(input.focus) : null,
      mood: input.mood ? number(input.mood) : null,
      updated_at: timestamp
    };
  } else {
    throw new Error("当前试用空间不支持这个操作");
  }

  audit(store, kind === "profile" ? "UPDATE" : "CREATE", kind, entityId);
  writeStore(store);
  return { id: entityId };
}

export const trialApi = {
  async bootstrap() {
    return bootstrap(readStore());
  },
  async create(kind: string, raw: unknown) {
    return create(readStore(), kind, raw);
  },
  async update(entityType: string, entityId: string, raw: unknown) {
    const store = readStore();
    const input = raw as Record<string, unknown>;
    if (entityType === "skills") {
      const skill = store.skills.find((item) => item.id === entityId);
      if (!skill) throw new Error("技能或兴趣不存在");
      const defaults = resolveSkillDefaults({
        title: text(input.title),
        kind: input.kind === "INTEREST" ? "INTEREST" : "SKILL",
        sphere: (["WORK", "LIFE"].includes(text(input.sphere)) ? text(input.sphere) : undefined) as SkillSphere | undefined,
        category: text(input.category),
        growthDimensionId: (["attr-career", "attr-experience", "attr-physique"].includes(text(input.growthDimensionId))
          ? text(input.growthDimensionId)
          : undefined) as SkillGrowthDimensionId | undefined
      });
      skill.title = text(input.title);
      skill.description = text(input.description);
      skill.kind = defaults.kind;
      skill.sphere = defaults.sphere;
      skill.category = text(input.category) || "综合";
      skill.growth_dimension_id = defaults.growthDimensionId;
    } else if (entityType === "contacts") {
      const contact = store.contacts.find((item) => item.id === entityId);
      if (!contact) throw new Error("客户不存在");
      contact.name = text(input.name);
      contact.organization = text(input.organization);
      contact.country = text(input.country);
      contact.contact_channel = text(input.contactChannel);
      contact.channel_account = text(input.channelAccount);
      contact.customer_status = text(input.customerStatus) as Contact["customer_status"];
      contact.importance = text(input.importance) as Contact["importance"];
      contact.main_products = text(input.mainProducts);
      contact.purchase_season = text(input.purchaseSeason);
      contact.annual_purchase = text(input.annualPurchase);
      contact.context = text(input.context);
      contact.next_action = text(input.nextAction);
      contact.next_followup = text(input.nextFollowup) || null;
    } else if (entityType === "customer-orders") {
      const order = store.customerOrders.find((item) => item.id === entityId);
      if (!order) throw new Error("订单不存在");
      const amount = text(input.amount);
      const [whole = "0", decimal = ""] = amount.split(".");
      order.contact_id = text(input.contactId);
      order.order_code = text(input.orderCode);
      order.title = text(input.title);
      order.images_json = JSON.stringify(Array.isArray(input.images) ? input.images : []);
      order.requirements = text(input.requirements);
      order.amount_minor = Number(whole) * 100 + Number(decimal.padEnd(2, "0"));
      order.currency = text(input.currency) as CustomerOrder["currency"];
      order.quantity = input.quantity ? number(input.quantity) : null;
      order.status = text(input.status) as CustomerOrder["status"];
      order.next_action = text(input.nextAction);
      order.next_followup = text(input.nextFollowup) || null;
      order.note = text(input.note);
      order.updated_at = now();
    } else if (entityType === "assets") {
      const asset = store.assets.find((item) => item.id === entityId);
      if (!asset) throw new Error("财务现状不存在");
      const amount = text(input.amount);
      const [whole = "0", decimal = ""] = amount.split(".");
      asset.name = text(input.name);
      asset.asset_type = (["CASH", "BANK", "INVESTMENT", "RECEIVABLE", "FIXED"].includes(
        text(input.assetType)
      )
        ? text(input.assetType)
        : "OTHER") as Asset["asset_type"];
      asset.amount_minor = Number(whole) * 100 + Number(decimal.padEnd(2, "0"));
      asset.currency = text(input.currency) || "CNY";
      asset.note = text(input.note);
      asset.as_of_date = text(input.asOfDate);
    } else if (entityType === "financial-plans") {
      const plan = store.financialPlans.find((item) => item.id === entityId);
      if (!plan) throw new Error("财务计划不存在");
      const amount = text(input.amount);
      const [whole = "0", decimal = ""] = amount.split(".");
      plan.title = text(input.title);
      plan.plan_type = input.planType === "SPENDING" ? "SPENDING" : "SAVING";
      plan.amount_minor = Number(whole) * 100 + Number(decimal.padEnd(2, "0"));
      plan.currency = text(input.currency) as FinancePlan["currency"];
      plan.cadence = text(input.cadence) as FinancePlan["cadence"];
      plan.target_date = text(input.targetDate) || null;
      plan.note = text(input.note);
      plan.status = text(input.status) as FinancePlan["status"];
      plan.updated_at = now();
    } else {
      throw new Error("当前实体暂不支持编辑");
    }
    audit(store, "UPDATE", entityType, entityId);
    writeStore(store);
    return { id: entityId };
  },
  async reorderContacts(orderedIds: string[]) {
    const store = readStore();
    const byId = new Map(store.contacts.map((contact) => [contact.id, contact]));
    if (orderedIds.length !== store.contacts.length || orderedIds.some((id) => !byId.has(id))) {
      throw new Error("客户列表已经变化，请刷新后重试排序");
    }
    store.contacts = orderedIds.map((id, index) => ({
      ...byId.get(id)!,
      sort_order: index + 1
    }));
    writeStore(store);
    return { ids: orderedIds };
  },
  async updateCustomerOrderStatus(orderId: string, status: string) {
    const store = readStore();
    const order = store.customerOrders.find((item) => item.id === orderId);
    if (!order) throw new Error("订单不存在");
    order.status = status as CustomerOrder["status"];
    order.updated_at = now();
    audit(store, "STATUS_CHANGE", "customer_orders", orderId);
    writeStore(store);
    return { id: orderId };
  },
  async uploadOrderImage(payload: { mimeType: string; dataBase64: string }) {
    return { url: `data:${payload.mimeType};base64,${payload.dataBase64}` };
  },
  async completeTask(taskId: string) {
    const store = readStore();
    const task = store.tasks.find((item) => item.id === taskId);
    if (!task) throw new Error("任务不存在");
    const existing = store.events.find(
      (event) => event.source_type === "TASK_COMPLETION" && event.factual_content === taskId
    );
    if (task.status === "COMPLETED" && existing) {
      return { taskId, eventId: existing.id, xp: 0, idempotent: true };
    }
    const timestamp = now();
    task.status = "COMPLETED";
    task.completed_at = timestamp;
    const eventId = id();
    store.events.unshift({
      id: eventId,
      event_type: "RESULT",
      occurred_at: timestamp,
      title: `完成任务：${task.title}`,
      factual_content: taskId,
      result_summary: "任务已按当前完成标准标记完成。",
      next_action: "",
      source_type: "TASK_COMPLETION",
      ai_assisted: 0,
      links_json: JSON.stringify([
        { entityType: "task", entityId: task.id },
        ...(task.project_id
          ? [{ entityType: "project", entityId: task.project_id }]
          : []),
        ...(task.goal_id ? [{ entityType: "goal", entityId: task.goal_id }] : []),
        ...(task.skill_id ? [{ entityType: "skill", entityId: task.skill_id }] : [])
        ,
        ...(task.contact_id ? [{ entityType: "contact", entityId: task.contact_id }] : [])
      ]),
      created_at: timestamp
    });
    let xp = 0;
    if (task.skill_id) {
      xp += awardTrialSkillEvidence(store, {
        skillId: task.skill_id,
        eventId,
        timestamp,
        baseXp: 10,
        difficulty: task.difficulty,
        impact: task.value_score,
        evidenceLevel: "PRACTICE",
        reason: "由已完成任务产生，经验值按难度、价值与当日重复次数计算。"
      });
    }
    const inferred = syncTrialExerciseSkills(store, {
      eventId,
      text: `${task.title} ${task.detail}`,
      timestamp,
      difficulty: task.difficulty,
      impact: task.value_score
    });
    xp += inferred.awardedXp;
    audit(store, "COMPLETE", "tasks", taskId);
    writeStore(store);
    return { taskId, eventId, xp, idempotent: false };
  },
  async updateTaskStatus(taskId: string, status: string) {
    const store = readStore();
    const task = store.tasks.find((item) => item.id === taskId);
    if (!task) throw new Error("任务不存在");
    task.status = status;
    if (status === "TODO") task.completed_at = null;
    audit(store, "STATUS_CHANGE", "tasks", taskId);
    writeStore(store);
    return { id: taskId };
  },
  async generateReview() {
    const store = readStore();
    const recent = store.events.slice(0, 40);
    if (recent.length === 0) throw new Error("还没有事实记录，暂时无法生成复盘草稿");
    const reviewId = id();
    const timestamp = now();
    const { start, end } = getRollingReviewPeriod(
      new Date(timestamp),
      store.profile?.timezone ?? "Asia/Shanghai"
    );
    const content = [
      "## 本周事实摘要",
      ...recent.slice(0, 8).map((event) => `- ${event.title}`),
      "",
      "## 下周候选重点",
      ...recent
        .filter((event) => event.next_action)
        .slice(0, 3)
        .map((event) => `- ${event.next_action}（依据：${event.title}）`)
    ].join("\n");
    store.reviews.unshift({
      id: reviewId,
      review_type: "WEEKLY",
      title: `${start} — ${end} 周复盘`,
      content,
      status: "DRAFT",
      period_start: start,
      period_end: end,
      source_event_ids: JSON.stringify(recent.map((event) => event.id)),
      created_at: timestamp
    });
    audit(store, "GENERATE_DRAFT", "reviews", reviewId);
    writeStore(store);
    return { id: reviewId };
  },
  async publishReview(reviewId: string) {
    const store = readStore();
    const review = store.reviews.find((item) => item.id === reviewId);
    if (!review) throw new Error("复盘不存在");
    review.status = "PUBLISHED";
    audit(store, "PUBLISH", "reviews", reviewId);
    writeStore(store);
    return { id: reviewId };
  },
  async remove(entityType: string, entityId: string) {
    const store = readStore();
    const collectionMap: Record<string, keyof TrialStore> = {
      goals: "goals",
      projects: "projects",
      tasks: "tasks",
      activity_events: "events",
      skills: "skills",
      reviews: "reviews",
      contacts: "contacts",
      customer_orders: "customerOrders",
      transactions: "transactions",
      assets: "assets",
      financial_plans: "financialPlans"
    };
    const key = collectionMap[entityType];
    if (!key) throw new Error("不支持删除这个实体");
    const collection = store[key] as unknown[];
    const index = collection.findIndex(
      (item) => (item as { id?: string }).id === entityId
    );
    if (index < 0) throw new Error("记录不存在");
    const [snapshot] = collection.splice(index, 1);
    const row = snapshot as { title?: string; name?: string; category?: string };
    store.trash.unshift({
      id: entityId,
      entityType,
      label: row.title ?? row.name ?? row.category ?? "未命名记录",
      deletedAt: now(),
      snapshot
    });
    audit(store, "SOFT_DELETE", entityType, entityId);
    writeStore(store);
    return { id: entityId };
  },
  async restore(entityType: string, entityId: string) {
    const store = readStore();
    const index = store.trash.findIndex(
      (item) => item.entityType === entityType && item.id === entityId
    );
    if (index < 0) throw new Error("回收站中没有这条记录");
    const [item] = store.trash.splice(index, 1);
    if (!item) throw new Error("记录不存在");
    const collectionMap: Record<string, keyof TrialStore> = {
      goals: "goals",
      projects: "projects",
      tasks: "tasks",
      activity_events: "events",
      skills: "skills",
      reviews: "reviews",
      contacts: "contacts",
      customer_orders: "customerOrders",
      transactions: "transactions",
      assets: "assets",
      financial_plans: "financialPlans"
    };
    const key = collectionMap[entityType];
    if (!key) throw new Error("不支持恢复这个实体");
    (store[key] as unknown[]).push(item.snapshot);
    audit(store, "RESTORE", entityType, entityId);
    writeStore(store);
    return { id: entityId };
  }
};
